
'use client'

import { useState, useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { FiMessageSquare, FiX, FiSend, FiPlus, FiArrowRight } from 'react-icons/fi'
import { FaWhatsapp } from 'react-icons/fa'
import { cn } from '@/lib/utils'

export default function SupportWidget() {
  let pathname = ''
  try {
    pathname = usePathname() || ''
  } catch (e) {
    console.error('SupportWidget: Error calling usePathname', e)
  }
  const [isOpen, setIsOpen] = useState(false)
  
  // Determinar panel actual
  const getPanel = () => {
    if (pathname?.startsWith('/hosting')) return 'hosting'
    if (['/dashboard', '/usuarios', '/planes', '/reportes', '/admins'].some(p => pathname?.startsWith(p))) return 'admin'
    return 'apis'
  }

  const panel = getPanel()

  // Mensaje inicial dinámico
  const getInitialMessage = () => {
    switch (panel) {
      case 'hosting': return '¡Hola! 👋 Soy tu experto en Hosting. ¿Te ayudo con tus archivos, bases de datos o WordPress?'
      case 'admin': return '¡Hola! 👋 Asistente administrativo listo. ¿Necesitas ayuda gestionando usuarios o viendo reportes?'
      default: return '¡Hola! 👋 Soy tu guía de MIAPI REST. ¿Necesitas ayuda con el Token, registro de empresas o validación SUNAT?'
    }
  }

  const [messages, setMessages] = useState<{ role: 'user' | 'assistant', content: string }[]>([
    { role: 'assistant', content: getInitialMessage() }
  ])

  // Reiniciar chat si cambia el panel sustancialmente
  useEffect(() => {
    setMessages([{ role: 'assistant', content: getInitialMessage() }])
  }, [panel])

  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages])

  const handleSend = async () => {
    if (!input.trim() || loading) return

    const userMessage = { role: 'user' as const, content: input }
    setMessages(prev => [...prev, userMessage])
    setInput('')
    setLoading(true)

    try {
      const res = await fetch('/api/support-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          messages: [...messages, userMessage],
          panel // Enviamos el contexto
        })
      })
      const data = await res.json()
      if (data.content) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.content }])
      }
    } catch (error) {
// ... (omitting catch for brevity in instruction, but keeping it in replacement)
      console.error("Chat error:", error)
      setMessages(prev => [...prev, { role: 'assistant', content: 'Lo siento, hubo un error. Intenta de nuevo.' }])
    }
    setLoading(false)
  }

  const openWhatsapp = () => {
    window.open(`https://wa.me/51917575562?text=Hola, estoy en el panel de ${panel === 'hosting' ? 'Hosting' : panel === 'admin' ? 'Administrador' : 'APIs'} y necesito ayuda técnico.`, '_blank')
  }

  // Componente para renderizar texto con formato básico (negrita y títulos)
  const FormattedMessage = ({ text }: { text: string }) => {
    // Dividir por líneas para manejar encabezados y listas
    const lines = text.split('\n')
    
    return (
      <div className="space-y-2">
        {lines.map((line, idx) => {
          // Manejar encabezados (### o ###)
          if (line.startsWith('###') || line.startsWith('##') || line.startsWith('#')) {
            const cleanLine = line.replace(/#+\s?/, '')
            return <h4 key={idx} className="font-bold text-sm uppercase tracking-wider text-blue-700 mt-3">{cleanLine}</h4>
          }
          
          // Manejar negritas (**texto**)
          const parts = line.split(/(\*\*.*?\*\*)/g)
          
          return (
            <p key={idx} className="leading-relaxed">
              {parts.map((part, i) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                  return <strong key={i} className="font-extrabold text-blue-900">{part.slice(2, -2)}</strong>
                }
                return part
              })}
            </p>
          )
        })}
      </div>
    )
  }

  return (
    <div className="fixed bottom-6 right-6 z-[100] flex flex-col items-end gap-4">
      {/* Ventana de Soporte */}
      {isOpen && (
        <Card className="w-[350px] sm:w-[400px] h-[550px] shadow-2xl overflow-hidden flex flex-col border-none animate-in slide-in-from-bottom-10 fade-in duration-300 rounded-3xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white relative">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-md">
                <FiMessageSquare className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Centro de Soporte</h3>
                <p className="text-xs text-blue-100 flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
                  Asistente IA Online
                </p>
              </div>
            </div>
          </div>

          {/* Opciones Rápidas */}
          <div className="p-4 bg-gray-50 border-b flex gap-2 overflow-x-auto no-scrollbar">
            <button 
              onClick={openWhatsapp}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-xl text-xs font-bold hover:bg-green-600 transition-colors whitespace-nowrap shadow-sm"
            >
              <FaWhatsapp className="h-4 w-4" /> WhatsApp Directo
            </button>
          </div>

          {/* Chat Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-white scrollbar-thin scrollbar-thumb-gray-200">
            {messages.map((msg, i) => (
              <div key={i} className={cn("flex", msg.role === 'user' ? "justify-end" : "justify-start")}>
                <div className={cn(
                  "max-w-[90%] p-4 text-sm",
                  msg.role === 'user' 
                    ? "bg-blue-600 text-white rounded-2xl rounded-tr-none shadow-md" 
                    : "bg-gray-50 text-gray-800 rounded-2xl rounded-tl-none border border-gray-100 shadow-sm"
                )}>
                  {msg.role === 'assistant' ? (
                    <FormattedMessage text={msg.content} />
                  ) : (
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 p-3 rounded-2xl rounded-tl-none flex gap-1">
                  <span className="h-1.5 w-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                  <span className="h-1.5 w-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                  <span className="h-1.5 w-1.5 bg-gray-400 rounded-full animate-bounce" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t">
            <div className="relative flex items-center gap-2">
              <input
                type="text"
                placeholder="Escribe tu duda aquí..."
                className="flex-1 bg-gray-50 border-none rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 transition-all outline-none pr-12"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              />
              <Button 
                size="icon" 
                onClick={handleSend}
                disabled={loading || !input.trim()}
                className="absolute right-1 h-10 w-10 rounded-xl bg-blue-600 hover:bg-blue-700 text-white shadow-lg"
              >
                <FiSend className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-[10px] text-center text-gray-400 mt-3 font-medium uppercase tracking-widest">
              Powered by MiApi Cloud Intelligence
            </p>
          </div>
        </Card>
      )}

      {/* Botón Flotante */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "h-14 w-14 rounded-full shadow-2xl transition-all duration-300 border-none",
          isOpen ? "bg-gray-800 rotate-90" : "bg-blue-600 hover:bg-blue-700 hover:scale-110"
        )}
      >
        {isOpen ? <FiX className="h-6 w-6" /> : <FiMessageSquare className="h-6 w-6" />}
      </Button>
    </div>
  )
}
